import { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { getProfile, updateProfile } from '../api/members'
import { FamilySection } from '../components/FamilySection'
import type { Member } from '../types'
import type { UpdateProfileRequest } from '../api/members'

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
  'DC','PR','VI','GU','AS','MP',
]

export function Profile() {
  const { user, refreshUser } = useAuth()
  const secretaryClubs = user?.secretaryClubs ?? []
  const [member, setMember] = useState<Member | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Form state
  const [form, setForm] = useState<UpdateProfileRequest>({
    first: '',
    last: '',
    address: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: '',
    votingPreference: null,
  })

  useEffect(() => {
    getProfile()
      .then((data) => {
        setMember(data)
        setForm({
          first: data.first,
          last: data.last,
          address: data.address,
          address2: data.address2,
          city: data.city,
          state: data.state,
          zip: data.zip,
          phone: data.phone,
          email: data.email,
          votingPreference: data.votingPreference,
        })
      })
      .catch(() => setError('Failed to load profile'))
      .finally(() => setLoading(false))
  }, [])

  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const { name, value } = e.target
    setForm((prev) => ({ ...prev, [name]: value }))
  }

  function handleVotingChange(value: 'paper' | 'electronic' | null) {
    setForm((prev) => ({ ...prev, votingPreference: value }))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')
    setSuccess('')
    setSaving(true)

    try {
      const updated = await updateProfile(form)
      setMember(updated)
      setSuccess('Profile updated successfully.')
      // Refresh the auth user so the header reflects any name/email changes
      await refreshUser()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
      setError(msg || 'Failed to save profile. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="text-center py-16">
        <p className="text-text-muted">Loading profile...</p>
      </div>
    )
  }

  if (!member) {
    return (
      <div className="text-center py-16">
        <p className="text-error">Unable to load profile.</p>
      </div>
    )
  }

  return (
    <>
      <Helmet><title>My Profile - ARPU</title></Helmet>
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-heading font-bold text-text mb-6">My Profile</h1>

        {/* Display-only info */}
        <div className="bg-surface-alt border border-border rounded-lg p-4 mb-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="block text-text-muted">AU ID</span>
              <span className="font-medium text-text">{member.auid}</span>
            </div>
            <div>
              <span className="block text-text-muted">Class</span>
              <span className="font-medium text-text">{member.class || 'N/A'}</span>
            </div>
            <div>
              <span className="block text-text-muted">Dues Status</span>
              {member.duesPd ? (
                <span className="font-medium text-success">Current</span>
              ) : (
                <span className="font-medium text-error">Past Due</span>
              )}
            </div>
            <div>
              <span className="block text-text-muted">Dues Paid Date</span>
              <span className="font-medium text-text">{member.duesPdDate || 'N/A'}</span>
            </div>
          </div>
        </div>

        {/* Club page management — shown only to club secretaries */}
        {secretaryClubs.length > 0 && (
          <div className="bg-surface-alt border border-border rounded-lg p-4 mb-6">
            <h2 className="font-heading font-semibold text-text mb-1">My Club Page</h2>
            <p className="text-sm text-text-muted mb-3">
              As club secretary, you can manage your club&apos;s public page — description,
              meeting info, contact details, social media links, photos, and events.
            </p>
            <div className="space-y-2">
              {secretaryClubs.map((c) => (
                <div key={c.clubNum} className="flex items-center justify-between gap-4">
                  <span className="text-sm font-medium text-text">{c.clubName}</span>
                  <Link
                    to={`/clubs/${c.slug}/edit`}
                    className="shrink-0 px-4 py-1.5 bg-primary text-text-inverse rounded-md hover:bg-primary-light transition-colors text-sm"
                  >
                    Edit Club Page
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}

        {error && (
          <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 mb-4 text-sm">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-success/10 border border-success/30 text-success rounded-md px-4 py-3 mb-4 text-sm">
            {success}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="First Name" name="first" value={form.first ?? ''} onChange={handleChange} required />
            <Field label="Last Name" name="last" value={form.last ?? ''} onChange={handleChange} required />
          </div>

          {/* Address */}
          <Field label="Address" name="address" value={form.address ?? ''} onChange={handleChange} />
          <Field label="Address Line 2" name="address2" value={form.address2 ?? ''} onChange={handleChange} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="col-span-2 sm:col-span-1">
              <Field label="City" name="city" value={form.city ?? ''} onChange={handleChange} />
            </div>
            <div>
              <label htmlFor="state" className="block text-sm font-medium text-text mb-1">State</label>
              <select
                id="state"
                name="state"
                value={form.state ?? ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
              >
                <option value="">--</option>
                {US_STATES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <Field label="Zip" name="zip" value={form.zip ?? ''} onChange={handleChange} />
            </div>
          </div>

          {/* Contact */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Phone" name="phone" type="tel" value={form.phone ?? ''} onChange={handleChange} />
            <Field label="Email" name="email" type="email" value={form.email ?? ''} onChange={handleChange} required />
          </div>

          {/* Voting Preference */}
          <fieldset>
            <legend className="block text-sm font-medium text-text mb-2">Voting Preference</legend>
            <div className="flex flex-wrap gap-6">
              <RadioOption
                label="Paper Ballot"
                checked={form.votingPreference === 'paper'}
                onChange={() => handleVotingChange('paper')}
              />
              <RadioOption
                label="Electronic Ballot"
                checked={form.votingPreference === 'electronic'}
                onChange={() => handleVotingChange('electronic')}
              />
              <RadioOption
                label="No Preference"
                checked={form.votingPreference === null}
                onChange={() => handleVotingChange(null)}
              />
            </div>
          </fieldset>

          {/* Submit */}
          <div className="pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-2.5 bg-primary hover:bg-primary-light text-text-inverse font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Family Membership */}
        <div className="mt-10 pt-8 border-t border-border">
          <FamilySection />
        </div>
      </div>
    </>
  )
}

function Field({ label, name, value, onChange, type = 'text', required }: {
  label: string
  name: string
  value: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  type?: string
  required?: boolean
}) {
  return (
    <div>
      <label htmlFor={name} className="block text-sm font-medium text-text mb-1">{label}</label>
      <input
        id={name}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full px-3 py-2 border border-border rounded-md bg-surface text-text focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
      />
    </div>
  )
}

function RadioOption({ label, checked, onChange }: {
  label: string
  checked: boolean
  onChange: () => void
}) {
  return (
    <label className="flex items-center gap-2 cursor-pointer text-sm text-text">
      <input
        type="radio"
        name="votingPreference"
        checked={checked}
        onChange={onChange}
        className="accent-primary"
      />
      {label}
    </label>
  )
}
